import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Input, Typography, Space, Form, Select, Flex, Card, Drawer } from "antd"
import { EditOutlined, DeleteOutlined, CloseOutlined } from "@ant-design/icons"
import { Text } from "ui/Typography"
import IconWithBg from "ui/IconWithBg"
import styles from "./index.module.css"
import { getGuideCategoryToBoats, getGuideBoatsListing, addCategory, editCategory, deleteCategory, selectAddCategoryLoading, selectEditCategoryLoading, selectDeleteCategoryLoading } from "store/boats"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { DSIcon } from "ui/Icon/DSIcon"

export default function ManageCategoryModal({ open, onCancel, onUpdate }) {
  ManageCategoryModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
  }
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const posting = useSelector((state) => state.boats?.category_to_boats?.loading)
  const categoryListing = useSelector((state) => state.boats.category_to_boats.items)
  const addingCategory = useSelector(selectAddCategoryLoading)
  const editingCategory = useSelector(selectEditCategoryLoading)
  const deletingCategory = useSelector(selectDeleteCategoryLoading)

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const [categories, setCategories] = useState([])

  const [addForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [editingIndex, setEditingIndex] = useState(null)
  const [originalEditName, setOriginalEditName] = useState("")
  const [isEditDirty, setIsEditDirty] = useState(false)
  const [isAddVisible, setIsAddVisible] = useState(false)
  const [deleteIndex, setDeleteIndex] = useState(null)
  const [moveTargetId, setMoveTargetId] = useState(null)

  // Fetch categories on mount
  useEffect(() => {
    dispatch(getGuideCategoryToBoats())
  }, [dispatch])

  // Sync local categories when store updates
  useEffect(() => {
    if (Array.isArray(categoryListing)) {
      const mappedCategories = categoryListing.map((c) => ({ id: c.id, name: c.name, items: c.boats_count ?? 0 }))
      setCategories(mappedCategories)
    }
  }, [categoryListing])
  // Add category - calls API immediately
  const handleAdd = async (values) => {
    const trimmedName = values.name.trim()

    try {
      // Add category immediately via API
      await dispatch(addCategory({ name: trimmedName }))
      toaster.success(t("equipment.category_added_successfully", { defaultValue: "Category added successfully" }))

      // Refresh listings
      dispatch(getGuideCategoryToBoats())
      dispatch(getGuideBoatsListing())

      setIsAddVisible(false)
      addForm.resetFields()
    } catch (error) {
      const errorMessage = error?.message || t("equipment.failed_to_add_category", { defaultValue: "Failed to add category" })
      console.error(errorMessage)
    }
  }

  // Edit category - calls API immediately
  const handleEdit = async (index, values) => {
    const trimmedName = values.name.trim()
    const target = categories[index]

    try {
      // Edit category immediately via API
      await dispatch(editCategory(target.id, { name: trimmedName }))
      toaster.success(t("equipment.category_updated_successfully", { defaultValue: "Category updated successfully" }))

      // Refresh listings
      dispatch(getGuideCategoryToBoats())
      dispatch(getGuideBoatsListing())

      setEditingIndex(null)
      editForm.resetFields()
    } catch (error) {
      const errorMessage = error?.message || t("equipment.failed_to_update_category", { defaultValue: "Failed to update category" })
      console.error(errorMessage)
    }
  }

  const handleDelete = (index) => {
    const target = categories[index]

    // Prevent deletion only if:
    // 1. This category has items AND
    // 2. There's only one category (nowhere to move items)
    if (target.items > 0 && categories.length === 1) {
      return
    }

    setDeleteIndex(index)
  }

  // Delete category - calls API immediately
  const confirmDelete = async (index) => {
    const target = categories[index]

    // Validate move target if needed
    if (target.items > 0 && !moveTargetId) {
      toaster.error(t("equipment.modal_errors.select_category_to_move"))
      return
    }

    try {
      const deleteData = {}
      if (target.items > 0 && moveTargetId) {
        deleteData.move_to_category_id = moveTargetId
      }

      // Delete category immediately via API
      await dispatch(deleteCategory(target.id, deleteData))
      toaster.success(t("equipment.category_deleted_successfully", { defaultValue: "Category deleted successfully" }))

      // Refresh listings
      dispatch(getGuideCategoryToBoats())
      dispatch(getGuideBoatsListing())

      setDeleteIndex(null)
      setMoveTargetId(null)
    } catch (error) {
      const errorMessage = error?.message || t("equipment.failed_to_delete_category", { defaultValue: "Failed to delete category" })
      console.error(errorMessage)
    }
  }

  // Handle cancel - simplified (no revert needed)
  const handleCancel = () => {
    setEditingIndex(null)
    setIsAddVisible(false)
    setDeleteIndex(null)
    setMoveTargetId(null)
    addForm.resetFields()
    editForm.resetFields()
    onCancel()
  }

  const content = (
      <FirstLoading loading={posting}>
        {!isAddVisible && categories.length > 0 && (
          <Flex className={styles.addCategoryWrapper}>
            <Button type="primary" onClick={() => setIsAddVisible(true)} className={styles.addCategoryBtn}>
              {t("equipment.add_category")}
            </Button>
          </Flex>
        )}

        <Space direction="vertical" className={styles.verticalSpace}>
          {/* Add new category */}

          {isAddVisible && (
            <div className={styles.addCategoryModal}>
              <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                <Flex justify="space-between" align="center" className={styles.addCategoryTitle}>
                  <Text size="lg" fw={500}>
                    {t("equipment.add_new_category")}
                  </Text>
                  <IconWithBg
                    icon={<CloseOutlined />}
                    className={styles.cancelBtn}
                    onClick={() => {
                      setIsAddVisible(false)
                      addForm.resetFields()
                    }}
                    aria-label={t("equipment.cancel")}
                  />
                </Flex>
                <div className={styles.addCategoryForm}>
                  <LabeledField
                    name="name"
                    labelKey={t("equipment.category_name")}
                    rules={[{ required: true, message: t("equipment.please_enter_category_name") }]}
                  >
                    <Input
                      autoFocus
                      placeholder="Category name"
                      className={styles.categoryInput}
                      onChange={() => addForm.setFields([{ name: "name", errors: [] }])}
                    />
                  </LabeledField>
                  <Space className="mt-xs">
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setIsAddVisible(false)
                        addForm.resetFields()
                      }}
                    >
                      {t("equipment.cancel")}
                    </Button>
                    <Button type="primary" htmlType="submit" loading={addingCategory} disabled={addingCategory}>
                      {t("equipment.save")}
                    </Button>
                  </Space>
                </div>
              </Form>
            </div>
          )}

          {/* Category list with empty state */}
          <WithEmptyStateWrapper
            name="category"
            hasItems={categories.length > 0}
            fallbackTitle={t("equipment.no_categories", { defaultValue: "No categories yet" })}
            fallbackButtonText={t("equipment.add_category")}
            fallbackButtonClickHandler={() => setIsAddVisible(true)}
            fallbackImageSrc={<DSIcon name="boat" width={80} height={80} />}
          >
            {categories.map((cat, idx) => (
              <Card key={cat.id || idx} className={styles.categoryCard} bodyStyle={{ padding: 0 }}>
                <Flex align="center" justify="space-between" className={styles.cardFlex}>
                  <div className={styles.categoryInfo}>
                    <span className={styles.categoryName}>{cat.name}</span>
                    <span className={styles.categoryItems}>({cat.items} items)</span>
                  </div>
                  {editingIndex === idx || deleteIndex === idx ? (
                    <IconWithBg
                      icon={<CloseOutlined />}
                      className={styles.editBtn}
                      onClick={() => {
                        if (editingIndex === idx) {
                          setEditingIndex(null)
                          editForm.resetFields()
                        } else if (deleteIndex === idx) {
                          setDeleteIndex(null)
                          setMoveTargetId(null)
                        }
                      }}
                      aria-label={t("equipment.cancel")}
                    />
                  ) : (
                    <Space size={16}>
                      <IconWithBg
                        icon={<EditOutlined />}
                        className={styles.editBtn}
                        onClick={() => {
                          setDeleteIndex(null)
                          setEditingIndex(idx)
                          setOriginalEditName(cat.name || "")
                          setIsEditDirty(false)
                          editForm.setFieldsValue({ name: cat.name })
                        }}
                        aria-label={t("equipment.edit")}
                      />
                      <IconWithBg
                        icon={<DeleteOutlined />}
                        className={styles.deleteBtn}
                        onClick={() => {
                          setEditingIndex(null)
                          handleDelete(idx)
                        }}
                        aria-label={t("equipment.delete")}
                        disabled={cat.items > 0 && categories.length === 1}
                        title={
                          cat.items > 0 && categories.length === 1
                            ? t("equipment.cannot_delete_category_with_items", {
                              defaultValue: "Cannot delete category with items. No other category to move items to."
                            })
                            : ""
                        }
                      />
                    </Space>
                  )}
                </Flex>
                {editingIndex === idx && (
                  <div className={styles.editCategoryModal}>
                    <div className={styles.editCategoryTitle}>{t("equipment.edit_category")}</div>
                    <Form
                      form={editForm}
                      layout="vertical"
                      onFinish={(values) => handleEdit(idx, values)}
                      initialValues={{ name: cat.name }}
                      onValuesChange={(changed, all) => {
                        const current = all.name || ""
                        setIsEditDirty(current.trim() !== (originalEditName || "").trim())
                      }}
                    >
                      <LabeledField
                        name="name"
                        labelKey={t("equipment.category_name")}
                        rules={[{ required: true, message: t("equipment.please_enter_category_name") }]}
                      >
                        <Input
                          autoFocus
                          className={styles.categoryInput}
                          onChange={() => editForm.setFields([{ name: "name", errors: [] }])}
                        />
                      </LabeledField>
                      <Space>
                        <Button variant="outlined" color="primary" onClick={() => setEditingIndex(null)}>{t("equipment.cancel")}</Button>
                        <Button type="primary" htmlType="submit" loading={editingCategory} disabled={!isEditDirty || editingCategory}>
                          {t("equipment.save")}
                        </Button>
                      </Space>
                    </Form>
                  </div>
                )}
                {deleteIndex === idx && (
                  <div className={styles.editCategoryModal}>
                    <div className={styles.editCategoryTitle}>{t("equipment.delete_category")}</div>
                    {cat.items > 0 ? (
                      <>
                        <Text>All {cat.items} items inside category will be moved to:</Text>
                        <Form layout="vertical" className="mt-m">
                          <Form.Item>
                            <Text fw={500}>Category</Text>
                            <Select
                              style={{ width: "100%" }}
                              placeholder="Select category"
                              value={moveTargetId || undefined}
                              onChange={(val) => setMoveTargetId(val)}
                            >
                              {categories
                                .filter((_, i) => i !== idx)
                                .map((c) => (
                                  <Select.Option key={c.id || c.name} value={c.id}>
                                    {c.name}
                                  </Select.Option>
                                ))}
                            </Select>
                          </Form.Item>
                        </Form>
                      </>
                    ) : (
                      <Text>This category has no items and can be safely deleted.</Text>
                    )}
                    <Flex justify="start" gap={5} className={styles.footerFlex}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          setDeleteIndex(null)
                          setMoveTargetId(null)
                        }}
                      >
                        {t("equipment.cancel")}
                      </Button>
                      <Button
                        type="primary"
                        danger
                        onClick={() => confirmDelete(idx)}
                        loading={deletingCategory}
                        disabled={deletingCategory || (cat.items > 0 && !moveTargetId)}
                      >
                        {cat.items > 0 && moveTargetId ? t("equipment.move_items_and_delete_category") : t("equipment.delete")}
                      </Button>
                    </Flex>
                  </div>
                )}
              </Card>
            ))}
          </WithEmptyStateWrapper>
        </Space>
      </FirstLoading>
  )

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={handleCancel}
        title={<Typography.Title level={4}>{t("equipment.manage_categories")}</Typography.Title>}
        placement="bottom"
        height="90vh"
        closable={true}
        closeIcon={<CloseOutlined />}
        maskClosable={false}
        className={styles.manageCategoriesDrawer}
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={<Typography.Title level={4}>{t("equipment.manage_categories")}</Typography.Title>}
      footer={null}
      width={800}
      centered
      className={styles.manageCategoriesModal}
      maskClosable={false}
    >
      {content}
    </Modal>
  )
}
